import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';

import { _isNotBlank, _isNotEmpty, _notNil } from '@/littledash';
import { errorToast, successToast } from '@/helpers';

import type { Animal, AnimalSex, AnimalSexLabel, AnimalSpecies } from '@/model/Animal.model';
import { AnimalSexLabelToCode } from '@/model/Animal.utils';
import type { MetadataField } from '@/model/Metadata.model';
import { MetadataTypes } from '@/model/Metadata.model';

import { MAX_CAGE_POPULATION } from '@/constants/cage';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { featuresSelector } from '@/support/Selectors';
import { createMeta, type HoTMeta } from '@/support/hot';
import type { CellValue } from '@/support/hot.types';
import { useEntitySchemaFieldsByType, useRefreshEntitySchemaOnMount } from '@/utils/entitySchema/useEntitySchema';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';

import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTable';
import type { CompatibleCellChange, CompatibleChangeSource } from '@/components/UI/SpreadSheet/Spreadsheet';
import type { SpreadsheetSettings } from '@/components/UI/SpreadSheet/SpreadSheet.model';
import { spreadSheetValidationTooltip } from '@/components/UI/SpreadSheet/SpreadSheet.utils';
import ApiErrorBanner, { ApiErrorType } from '@/components/ApiErrorBanner/ApiErrorBanner';
import type { CageCardCollection } from '@/components/Cages/Show/Cages';
import Loading from '@/components/Loading';
import AddAnimalsTable from '@/components/Colony/Edit/AddAnimalsTable';
import {
  type AddAnimalRow,
  getMetaData,
  getRequiredMetaColumns,
  initAnimalSettings,
  initialAddAnimalData,
} from '@/components/Colony/Colony.utils';

/**
 * The spreadsheet is built for a full page, so its default height leaves a large
 * empty band under a single row once it is inside a modal.
 */
const MODAL_TABLE_HEIGHT = 320;

/**
 * Handsontable keeps this many blank rows at the end of the grid. They carry the "+" control
 * in place of a row number and are never validated or submitted.
 */
const SPARE_ROWS = 1;

/** Source Handsontable reports for a row created by the "+" row-header control. */
const ADD_ROW_SOURCE = 'add-row.click';

/** Matches the names this modal generates, so a renamed row is left out of the numbering. */
const generatedAnimalName = /^Animal (\d+)$/;

const unknownSexSelector = createSelector([featuresSelector], (features) => ({
  unknownSexEnabled: features?.unknown_sex === true,
}));

interface AnimalPostData {
  cage_id?: string;
  name: string;
  dob?: string | null;
  sex?: AnimalSex;
  strain_id?: string;
  metadata?: Array<{ glossary_id: string; value: unknown }>;
}

interface BulkAddSubjectsProps {
  cage: CageCardCollection;
  handleCallback?: (response: { data: Array<Animal> }, cage: CageCardCollection) => void;
  closeModal: () => void;
}

const pluralize = (count: number, word: string): string => `${count} ${word}${count !== 1 ? 's' : ''}`;

const CAGE_AT_LIMIT_MESSAGE = 'This cage has reached its animal population limit';

const getPopulationSubText = (currentPopulation: number): string | undefined => {
  if (currentPopulation >= MAX_CAGE_POPULATION) {
    return CAGE_AT_LIMIT_MESSAGE;
  }
  const spacesLeft = MAX_CAGE_POPULATION - currentPopulation;
  return `There are currently ${pluralize(currentPopulation, 'animal')} in this cage. ${spacesLeft} more spaces are available.`;
};

/**
 * Adds animals to a single cage from the cage cards, in both the study and colony views.
 * Renders the same grid as the "Add animals" step of "Add new cages and animals", seeded
 * with one row for the cage in question.
 */
const BulkAddSubjects: FC<BulkAddSubjectsProps> = ({ cage, handleCallback, closeModal }) => {
  const { unknownSexEnabled } = useSelector(unknownSexSelector);
  useRefreshEntitySchemaOnMount();
  const entityFieldRequiredMap = useEntitySchemaFieldsByType('animal');

  const tableRef = useRef<HotTableRef>(null);

  // Neither source is reliable on its own: `counts` is not refreshed when animals are added from
  // this modal, and `subjects.data` can be narrowed by the cage card filters.
  const currentCagePopulation = Math.max(cage.counts?.animals ?? 0, cage.subjects?.data?.length ?? 0);
  const isCageFull = currentCagePopulation >= MAX_CAGE_POPULATION;

  const [data] = useState<Array<AddAnimalRow>>(() =>
    initialAddAnimalData({ name: cage.name, catalog: cage.catalog, counts: { animals: currentCagePopulation } })
  );
  const [glossary, setGlossary] = useState<Array<MetadataField> | null>(null);
  const [metadataAvailableToAdd, setMetadataAvailableToAdd] = useState<Array<MetadataField> | null>(null);
  const [isInvalid, setIsInvalid] = useState(false);
  const [enteredRowCount, setEnteredRowCount] = useState(1);
  const [glossaryLoading, setGlossaryLoading] = useState(true);
  const [glossaryLoadFailed, setGlossaryLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  const { response: speciesResponse, loading: speciesLoading } = useApiHook({
    endpoint: 'GET /api/v1/species',
    invokeOnInit: true,
    query: { include: ['strains'] },
    options: { onError: { throw: false, capture: true, toast: false, slug: 'bulk-add-subjects-species-load' } },
  });

  const species = useMemo<Array<AnimalSpecies>>(
    () => (speciesResponse?.type === 'success' ? (speciesResponse.body.data as unknown as Array<AnimalSpecies>) : []),
    [speciesResponse]
  );

  // An empty glossary is indistinguishable from a failed fetch once it reaches the grid, and a
  // missing required metadata column would let animals be created without values the team marked
  // mandatory. A failure is therefore recorded rather than folded into an empty list.
  useEffect(() => {
    let cancelled = false;
    getMetaData(MetadataTypes.subject_meta)
      .then((metadataResponse) => {
        if (cancelled) {
          return;
        }
        if (metadataResponse?.type === 'success') {
          setGlossary((metadataResponse.body ?? []) as Array<MetadataField>);
        } else {
          setGlossaryLoadFailed(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setGlossaryLoadFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setGlossaryLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const animalFieldsLoadFailed = glossaryLoadFailed || speciesResponse?.type === 'error';

  // Held back until the glossary lands so required metadata is a column from the first render.
  const { settings, unusedMetadata } = useMemo<{
    settings: SpreadsheetSettings | null;
    unusedMetadata: Array<MetadataField>;
  }>(() => {
    if (!_notNil(glossary)) {
      return { settings: null, unusedMetadata: [] };
    }
    const result = initAnimalSettings(
      species,
      true,
      true,
      getRequiredMetaColumns(glossary),
      glossary,
      undefined,
      entityFieldRequiredMap,
      unknownSexEnabled
    );
    return {
      settings: { ...result.settings, height: MODAL_TABLE_HEIGHT, minSpareRows: SPARE_ROWS },
      unusedMetadata: result.metadata ?? glossary,
    };
  }, [species, glossary, entityFieldRequiredMap, unknownSexEnabled]);

  // The grid narrows this list as columns are added, so its own choice wins once made.
  const metadataToOffer = metadataAvailableToAdd ?? unusedMetadata;

  /**
   * Handsontable adds and removes rows in place on the array it was handed, so `data` is the
   * live grid contents. The blank spare row carrying the "+" control is dropped here: a row is
   * only ever nameless while it is spare, since every other row fails validation without a name.
   */
  const enteredRows = (): Array<AddAnimalRow> =>
    data.reduce<Array<AddAnimalRow>>((acc, row) => {
      if (_isNotBlank(row?.name)) {
        acc.push(row);
      }
      return acc;
    }, []);

  /**
   * Handsontable edits `data` without React noticing, so the count is refreshed by hand as rows
   * come and go. This is what keeps the Save button in step with the grid.
   */
  const syncEnteredRowCount = (): void => {
    setEnteredRowCount(enteredRows().length);
  };

  /**
   * Counts up from the highest `Animal <n>` in the grid rather than from the new row's position,
   * so a name is not handed out twice after a row in the middle has been removed.
   */
  const nextAnimalName = (): string => {
    const highestSuffix = data.reduce((highest, row) => {
      const suffix = generatedAnimalName.exec(row?.name ?? '')?.[1];
      return _notNil(suffix) ? Math.max(highest, Number(suffix)) : highest;
    }, currentCagePopulation);

    return `Animal ${highestSuffix + 1}`;
  };

  /**
   * Fills a row created by the "+" control with the contents of the row above it, so a user
   * entering littermates only has to change what differs. The animal name is the one field
   * that must stay distinct, so it is generated rather than copied.
   */
  const copyRowAbove = (index: number): void => {
    const hot = tableRef.current?.hotInstance;
    if (!_notNil(hot) || index < 1) {
      return;
    }

    const changes: Array<[number, number, CellValue]> = [];
    for (let column = 0; column < hot.countCols(); column++) {
      const prop = hot.colToProp(column);
      if (prop === 'name') {
        changes.push([index, column, nextAnimalName()]);
      } else if (prop !== 'id') {
        const value = hot.getDataAtCell(index - 1, column);
        if (_notNil(value)) {
          changes.push([index, column, value]);
        }
      }
    }

    hot.setDataAtCell(changes, ADD_ROW_SOURCE);
  };

  const handleAfterCreateRow = (index: number, _amount: number, source?: CompatibleChangeSource): void => {
    if (source === ADD_ROW_SOURCE) {
      copyRowAbove(index);
    }
  };

  /**
   * Refreshes the row count for every edit, including typing into the spare row, which is how a
   * new animal enters the grid without a row ever being created.
   *
   * Rows copied by the "+" control are also validated here rather than in `afterCreateRow`:
   * Handsontable runs validators asynchronously, so validating straight after asking for the copy
   * reads the row while it is still blank and leaves every cell flagged as required.
   */
  const handleAfterChange = (changes: Array<CompatibleCellChange> | null, source: CompatibleChangeSource): void => {
    if (!_notNil(changes)) {
      return;
    }
    syncEnteredRowCount();

    const hot = tableRef.current?.hotInstance;
    if (source !== ADD_ROW_SOURCE || !_notNil(hot)) {
      return;
    }
    hot.validateRows(Array.from(new Set(changes.map(([row]) => row))));
  };

  const { invoke: postAnimals, response: postResponse } = useApiHook({
    endpoint: 'POST /api/v1/animals',
    invokeOnInit: false,
    options: { onError: { throw: false, capture: true, toast: false, slug: 'bulk-add-subjects-create' } },
  });

  /**
   * The create endpoint keys metadata by glossary api_id while the grid stores values by
   * encoded column title, so values are resolved against the full glossary rather than the
   * "still available to add" subset.
   */
  const buildMetadataPayload = (meta: AddAnimalRow['meta']): Array<{ glossary_id: string; value: unknown }> => {
    if (!_isNotEmpty(meta) || !_notNil(settings) || !_notNil(settings.columns)) {
      return [];
    }
    const glossaryIdToApiId = (glossary ?? []).reduce<Record<string, string>>((acc, field) => {
      if (_notNil(field.id) && _notNil(field.api_id)) {
        acc[String(field.id)] = field.api_id;
      }
      return acc;
    }, {});

    return (createMeta(meta, settings.columns) as Array<HoTMeta>).reduce<
      Array<{ glossary_id: string; value: unknown }>
    >((acc, entry) => {
      if (_notNil(entry.glossary_id)) {
        acc.push({
          glossary_id: glossaryIdToApiId[String(entry.glossary_id)] ?? String(entry.glossary_id),
          value: entry.value,
        });
      }
      return acc;
    }, []);
  };

  const mapAnimalPostData = (rows: Array<AddAnimalRow>): Array<AnimalPostData> =>
    rows.reduce<Array<AnimalPostData>>((acc, { name, sex, dob, species: animalSpecies, strain, meta }) => {
      const animal: AnimalPostData = {
        cage_id: cage.api_id,
        name,
        dob: dob ?? null,
      };

      if (_notNil(sex)) {
        animal.sex = AnimalSexLabelToCode[sex as AnimalSexLabel];
      }

      if (_notNil(animalSpecies)) {
        const matchingSpecies = species.find((s) => s.name === animalSpecies);
        const matchingStrain = matchingSpecies?.strains?.find((s) => s.name === strain);
        if (_notNil(matchingStrain?.api_id)) {
          animal.strain_id = matchingStrain.api_id;
        }
      }

      const metadata = buildMetadataPayload(meta);
      if (_isNotEmpty(metadata)) {
        animal.metadata = metadata;
      }

      acc.push(animal);
      return acc;
    }, []);

  const saveAnimals = async () => {
    setSaving(true);
    const animals = mapAnimalPostData(enteredRows());
    const result = await postAnimals({ body: { animals } }).catch(() => undefined);
    setSaving(false);

    if (result?.type !== 'success') {
      errorToast('Could not create animals');
      return;
    }

    successToast(`Created ${pluralize(animals.length, 'animal')} in ${cage.name}`);
    // The endpoint returns the created animals as a bare array. Its generated shape differs from
    // the hand-written `Animal` the cage cards are built from only in field nullability.
    handleCallback?.({ data: result.body as unknown as Array<Animal> }, cage);
  };

  // The "+" control has no notion of the cage limit, so the grid is capped at the spaces left.
  // A full cage still keeps one row so there is something to show.
  const maxRows = Math.max(MAX_CAGE_POPULATION - currentCagePopulation, 1) + SPARE_ROWS;

  return (
    <ModalContainer size="wide" className="bg-white" testKey="bulk-add-subjects">
      <ModalHeader
        title={`Add animals to ${cage.name}`}
        subText={getPopulationSubText(currentCagePopulation)}
        closeModal={closeModal}
        closeDisabled={saving}
        className="pa3 bb b--moon-gray w-100"
      />

      {postResponse?.type === 'error' && <ApiErrorBanner className="ma3" error={postResponse.error} />}

      {animalFieldsLoadFailed ? (
        <ApiErrorBanner
          className="ma3"
          errorType={ApiErrorType.FETCH}
          text="The animal fields for this cage could not be loaded, so animals cannot be added right now. Please close this window and try again."
        />
      ) : speciesLoading || glossaryLoading || !_notNil(settings) ? (
        <div style={{ height: MODAL_TABLE_HEIGHT }}>
          <Loading txt="Loading animal fields..." />
        </div>
      ) : (
        <div className="ow-spreadsheet-styles pa3">
          <AddAnimalsTable
            data={data as unknown as Array<Record<string, unknown>>}
            settings={settings}
            innerRef={tableRef}
            metadata={metadataToOffer}
            setMetadata={setMetadataAvailableToAdd}
            setInvalid={setIsInvalid}
            afterChange={handleAfterChange}
            afterCreateRow={handleAfterCreateRow}
            afterRemoveRow={syncEnteredRowCount}
            maxRows={maxRows}
            enableRowHeaderControls
          />
        </div>
      )}

      <ModalActions
        className="pa3 bt b--moon-gray w-100"
        onSubmit={saveAnimals}
        onCancel={closeModal}
        submitBtnText="Save"
        cancelBtnText="Cancel"
        submitButtonProps={{
          loading: saving,
          disabled:
            saving || isInvalid || enteredRowCount === 0 || !_notNil(settings) || isCageFull || animalFieldsLoadFailed,
          tooltip: isCageFull ? CAGE_AT_LIMIT_MESSAGE : isInvalid ? spreadSheetValidationTooltip : undefined,
        }}
        cancelButtonProps={{ disabled: saving }}
      />
    </ModalContainer>
  );
};

export default BulkAddSubjects;
